import { vehicles } from '@trashlab/api/src/domains/asset/schemas/vehicles';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { assets } from '@trashlab/api/src/domains/asset/schemas/assets';
import { consumer_cart } from '@trashlab/api/src/domains/consumer/schemas/consumer-cart';
import { workflow_kind } from '@trashlab/api/src/domains/core/schemas/base-template';
import { customer } from '@trashlab/api/src/domains/customer/schemas/customer';
import { customerSite } from '@trashlab/api/src/domains/customer/schemas/customer-site';
import { organization_site } from '@trashlab/api/src/domains/dispatch/schemas/organization-site';
import { driver } from '@trashlab/api/src/domains/driver/schemas/driver';
import { workflow_state } from '@trashlab/api/src/domains/driver/schemas/workflow-state';
import { identity_user } from '@trashlab/api/src/domains/identity/schemas/identity-user';
import { hauler_location } from '@trashlab/api/src/domains/identity/schemas/hauler-location';
import { dump_ticket } from '@trashlab/api/src/domains/orders/schemas/dump-ticket';
import { recurring_service } from '@trashlab/api/src/domains/orders/schemas/recurring-service';
import { service_line } from '@trashlab/api/src/domains/orders/schemas/service-line';
import { rental_chain } from '@trashlab/api/src/domains/orders/schemas/rental-chain';
import { service_line_v2 } from '@trashlab/api/src/domains/orders/schemas/service-line-v2';
import { sub_hauler } from '@trashlab/api/src/domains/orders/schemas/sub-hauler';
import { customer_order_request } from '@trashlab/api/src/domains/portal/schemas/customer-order-request';
import { material } from '@trashlab/api/src/domains/pricing/schemas/material';
import { product } from '@trashlab/api/src/domains/pricing/schemas/product';
import { sku_v2 } from '@trashlab/api/src/domains/pricing/schemas/sku-v2';
import { business_line_config } from '@trashlab/api/src/domains/business-lines/schemas/business-line';
import { sql } from 'drizzle-orm';
import {
  AnyPgColumn,
  boolean,
  check,
  date,
  doublePrecision,
  foreignKey,
  index,
  integer,
  unique,
  jsonb,
  pgEnum,
  pgTable,
  text,
  time,
  timestamp,
  uuid,
  varchar,
  bigint,
} from 'drizzle-orm/pg-core';
import { recurringWorkOrder } from '@trashlab/api/src/domains/orders/schemas/recurring-work-order';
import { customer_contact } from '@trashlab/api/src/domains/customer/schemas/customer-contact';
import { route } from '@trashlab/api/src/domains/routing/schemas/route';
import { order_status } from './order-status.enum';
import { order_type_config } from './order-type-config';

export const order_pricing_model = pgEnum('order_pricing_model', [
  'DEFAULT',
  'CUSTOMER_SPECIFIC',
]);

export const orders = pgTable(
  'orders',
  {
    ...defaultFields.fieldsWithTenantId(),
    number: integer('number').notNull(),
    vehicleId: uuid('vehicleId').references(() => vehicles.id),
    requestedByContactId: uuid('requestedByContactId').references(
      () => customer_contact.id,
    ),
    assignedDriverId: uuid('assignedDriverId'),
    dispatchNotes: text('dispatchNotes').default('').notNull(),
    isDriverNoteUrgent: boolean('isDriverNoteUrgent').notNull().default(false),
    driverNotes: text('driverNotes').default('').notNull(),
    driverNotesRich: jsonb('driverNotesRich'),
    serviceDate: date('serviceDate'),
    initiallyRequestedDate: date('initiallyRequestedDate'),
    status: order_status('status').default('PENDING').notNull(),
    totalDays: integer('totalDays').default(0).notNull(),
    driverRouteIndex: doublePrecision('driverRouteIndex')
      .default(
        sql`((EXTRACT(epoch FROM now()) * (1000)::double precision) + (1001)::double precision)`,
      )
      .notNull(),
    preDestinationSiteId: uuid('preDestinationSiteId'),
    workflowStateId: uuid('workflowStateId').notNull(),
    workflowKind: workflow_kind('workflowKind').default('DELIVERY').notNull(),
    pricingModel: order_pricing_model('pricingModel')
      .default('DEFAULT')
      .notNull(),
    incompleteReason: text('incompleteReason').default('').notNull(),
    shouldPreDestinationUseLastJob: boolean('shouldPreDestinationUseLastJob')
      .default(false)
      .notNull(),
    /**
     * @deprecated use dumpSiteId or storedBoxOrganizationSiteId instead
     */
    postDestinationSiteId: uuid('postDestinationSiteId'),
    dumpSiteId: uuid('dumpSiteId'),
    customerSiteId: uuid('customerSiteId'),
    destinationJobSiteId: uuid('destinationJobSiteId'),
    lastModifiedBy: uuid('lastModifiedBy'),
    preferredTimeEnd: time('preferredTimeEnd'),
    preferredTimeStart: time('preferredTimeStart'),
    createdBy: uuid('createdBy'),
    onlineOrderId: uuid('onlineOrderId'),
    customReferenceId: varchar('customReferenceId', { length: 255 }),
    scheduledDumpDate: date('scheduledDumpDate'),
    assignedDumpDriverId: uuid('assignedDumpDriverId'),
    driverRouteIndexForDump: doublePrecision('driverRouteIndexForDump')
      .default(
        sql`(((EXTRACT(epoch FROM now()) * (1000)::double precision) + (1001)::double precision) * ('-1'::integer)::double precision)`,
      )
      .notNull(),
    separateDumpScheduling: boolean('separateDumpScheduling')
      .default(false)
      .notNull(),
    searchables2: text('searchables2'),
    serviceLineId: uuid('serviceLineId').references(
      (): AnyPgColumn => service_line.id,
    ),
    rootServiceLineId: uuid('rootServiceLineId').references(
      (): AnyPgColumn => service_line.id,
    ),
    generatedByRoute: boolean('generatedByRoute').default(false).notNull(),
    createdByRecurringServiceId: uuid('createdByRecurringServiceId'),
    dispatchedFromRouteId: uuid('dispatchedFromRouteId'),
    isCompleted: boolean('isCompleted'),
    orderNumber: varchar('orderNumber', { length: 255 }).notNull(),
    customOrderDescription: text('customOrderDescription'),
    flagDispatchNotes: boolean('flagDispatchNotes').default(false).notNull(),
    workflowEndsService: boolean('workflowEndsService').notNull(),
    workflowStartsService: boolean('workflowStartsService').notNull(),
    customerId: uuid('customerId'),
    driverAddedNotes: text('driverAddedNotes').default('').notNull(),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    generatedByOnlineCartId: uuid('generatedByOnlineCartId'),
    freezeCharges: boolean('freezeCharges').default(false).notNull(),
    generatedByCustomerOrderRequestId: uuid(
      'generatedByCustomerOrderRequestId',
    ),
    dumpTicketId: uuid('dumpTicketId'),
    fakeColumn: boolean('fakeColumn').default(false).notNull(),
    completedAt: timestamp('completedAt', {
      withTimezone: true,
      mode: 'string',
    }),
    pickupDateBillingOverride: date('pickupDateBillingOverride'),
    subHaulerId: uuid('subHaulerId'),
    deliveryProductId: uuid('deliveryProductId'),
    deliveryAssetId: uuid('deliveryAssetId'),
    deliverySkuId: uuid('deliverySkuId'),
    deliveryMaterialId: uuid('deliveryMaterialId'),
    pickupProductId: uuid('pickupProductId'),
    pickupAssetId: uuid('pickupAssetId'),
    pickupSkuId: uuid('pickupSkuId'),
    pickupMaterialId: uuid('pickupMaterialId'),
    recurringWorkOrderId: uuid('recurringWorkOrderId').references(
      () => recurringWorkOrder.id,
    ),
    scheduledPickupDate: date('scheduledPickupDate'),
    haulerLocationId: uuid('haulerLocationId'),
    perWorkOrderPoNumber: varchar('perWorkOrderPoNumber', { length: 255 }),
    /** Quantity of items for the order */
    quantity: integer('quantity').default(1).notNull(),
    unpaidBalanceCents: bigint('unpaidBalanceCents', {
      mode: 'number',
    }).default(0),

    //Option:  this is the yard that the container was stored in, if the removed container was taken to the yard.
    storedBoxOrganizationSiteId: uuid('storedBoxOrganizationSiteId').references(
      () => organization_site.id,
    ),

    // This is the truck that was used to dump the order
    dumpVehicleId: uuid('dumpVehicleId').references(() => vehicles.id),

    // This is the truck that was used to service the order
    serviceVehicleId: uuid('serviceVehicleId').references(() => vehicles.id),

    // This is the contact that created the order
    orderContactId: uuid('orderContactId').references(
      () => customer_contact.id,
    ),

    // Cost tracking fields
    estimatedDurationMinutes: integer('estimatedDurationMinutes'),
    actualDurationMinutes: integer('actualDurationMinutes'),
    actualStartTime: timestamp('actualStartTime', { withTimezone: true }),
    actualEndTime: timestamp('actualEndTime', { withTimezone: true }),
    startMileage: integer('startMileage'),
    endMileage: integer('endMileage'),
    totalMiles: doublePrecision('totalMiles'),
    rentalChainId: uuid('rentalChainId').references(
      (): AnyPgColumn => rental_chain.id,
    ),
    /** Link to the service line v2 anchor row */
    serviceLineV2Id: uuid('serviceLineV2Id').references(
      (): AnyPgColumn => service_line_v2.id,
    ),
    /** Business line this order belongs to */
    businessLineId: uuid('businessLineId').references(
      () => business_line_config.id,
    ),

    /**
     * When true, the order workflow will skip the dump step, allowing drivers to complete
     * pickup orders without visiting a dump site.
     */
    skipDumpStep: boolean('skipDumpStep').default(false).notNull(),

    /**
     * Optional reference to a custom order type configuration.
     * When set, the order uses custom display name and color from the config.
     * When null, the order uses the default display based on workflowKind.
     *
     * This references an immutable versioned config record - updates to configs
     * create new version records, so existing orders always show the name/color
     * from when they were created.
     */
    orderTypeConfigId: uuid('orderTypeConfigId').references(
      () => order_type_config.id,
    ),
    plannedStartTime: time('plannedStartTime'),
  },
  (table) => {
    return {
      site_merge_page_idx: index('orders_site_merge_page_idx').on(
        table.tenantId,
        table.customerSiteId,
        table.id,
      ),
      site_technician_note_history_idx: index(
        'orders_site_technician_note_history_idx',
      )
        .on(
          table.tenantId,
          table.customerSiteId,
          table.serviceDate.desc(),
          table.id.desc(),
        )
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.customerSiteId} IS NOT NULL AND ${table.serviceDate} IS NOT NULL AND ${table.driverAddedNotes} ~ '[^[:space:]]'`,
        ),
      site_service_history_idx: index('orders_site_service_history_idx')
        .using(
          'btree',
          table.tenantId,
          table.customerSiteId,
          table.serviceDate.desc(),
          sql`COALESCE(${table.preferredTimeStart}, '00:00:00'::time) DESC`,
          table.createdAtTs.desc(),
          table.id.desc(),
        )
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.status} <> 'CANCELED' AND ${table.customerSiteId} IS NOT NULL AND ${table.serviceDate} IS NOT NULL AND ${table.workflowKind} IN ('SERVICE_LIQUID_WASTE', 'CUSTOM')`,
        ),
      destination_site_merge_page_idx: index(
        'orders_destination_site_merge_page_idx',
      ).on(table.tenantId, table.destinationJobSiteId, table.id),
      order_trigram: index('order_trigram').using(
        'gin',
        table.tenantId,
        table.searchables2.op('gin_trgm_ops'),
      ),
      assigned_driver_id_idx: index('orders_assigned_driver_id_idx').on(
        table.assignedDriverId,
      ),
      consoleNavigationMainGlobalDayIdx: index(
        'orders_console_navigation_main_global_day_idx',
      )
        .on(table.serviceDate, table.assignedDriverId, table.tenantId, table.id)
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.assignedDriverId} IS NOT NULL AND ${table.workflowKind} = 'SERVICE_LIQUID_WASTE' AND ${table.status} IN ('PENDING', 'IN_PROGRESS', 'SERVICED', 'INCOMPLETE', 'BILLED', 'PAUSED')`,
        ),
      consoleNavigationMainTenantDayIdx: index(
        'orders_console_navigation_main_tenant_day_idx',
      )
        .on(table.tenantId, table.serviceDate, table.status)
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.assignedDriverId} IS NOT NULL AND ${table.workflowKind} = 'SERVICE_LIQUID_WASTE' AND ${table.status} IN ('PENDING', 'IN_PROGRESS', 'SERVICED', 'INCOMPLETE', 'BILLED', 'PAUSED')`,
        ),
      createdAtTs_idx: index('orders_createdAtTs_idx').on(table.createdAtTs),
      onboardingSuccessGlobalCreatedIdx: index(
        'orders_onboarding_success_global_created_idx',
      )
        .on(table.createdAtTs, table.tenantId, table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      onboardingSuccessDispatchTenantDayIdx: index(
        'orders_onboarding_success_dispatch_tenant_day_idx',
      )
        .on(table.tenantId, table.serviceDate, table.id)
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.status} <> 'CANCELED' AND (${table.assignedDriverId} IS NOT NULL OR ${table.assignedDumpDriverId} IS NOT NULL)`,
        ),
      tenant_createdAtTs_id_active_idx: index(
        'orders_tenant_createdAtTs_id_active_idx',
      )
        .on(table.tenantId, table.createdAtTs.desc(), table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_updatedAtTs_id_active_idx: index(
        'orders_tenant_updatedAtTs_id_active_idx',
      )
        .on(table.tenantId, table.updatedAtTs.desc(), table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      customer_id_idx: index('orders_customer_id_idx').on(
        table.tenantId,
        table.customerId,
      ),
      customer_merge_page_idx: index('orders_customer_merge_page_idx').on(
        table.tenantId,
        table.customerId,
        table.id,
      ),
      tenant_customer_active_created_idx: index(
        'orders_tenant_customer_active_created_idx',
      )
        .on(table.tenantId, table.customerId, table.createdAtTs.desc())
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.customerId} IS NOT NULL`,
        ),
      customer_site_id_idx: index('orders_customer_site_id_idx').on(
        table.customerSiteId,
      ),
      destinationJobSiteId_idx: index('orders_destinationJobSiteId_idx').on(
        table.destinationJobSiteId,
      ),
      service_date_tenant_status_idx: index(
        'orders_service_date_tenant_status_idx',
      ).on(table.tenantId, table.serviceDate, table.status),
      tenantId_serviceDate_desc_idx: index('idx_orders_tenant_serviceDate_desc')
        .on(table.tenantId, table.serviceDate.desc())
        .where(sql`status <> 'CANCELED'`),
      dispatch_query_idx: index('idx_orders_dispatch_query').on(
        table.tenantId,
        table.status,
        table.serviceDate,
        table.scheduledDumpDate,
        table.separateDumpScheduling,
      ),
      // INCLUDE (id, dumpTicketId, serviceLineId, rootServiceLineId) lives in the migration.
      home_profit_covering_idx: index('orders_home_profit_covering_idx').on(
        table.tenantId,
        table.status,
        table.serviceDate,
      ),
      null_service_date_idx: index('idx_orders_null_service_date')
        .on(table.tenantId, table.status)
        .where(sql`"serviceDate" IS NULL`),
      unscheduled_initially_requested_idx: index(
        'orders_unscheduled_initially_requested_idx',
      )
        .on(
          table.tenantId,
          table.initiallyRequestedDate,
          table.driverRouteIndex,
        )
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.status} <> 'CANCELED' AND ${table.serviceDate} IS NULL AND ${table.initiallyRequestedDate} IS NOT NULL`,
        ),
      tenantId_idx: index('orders_tenantId_idx').on(table.tenantId),
      tenant_id_idx: index('orders_tenant_id_idx').on(table.tenantId, table.id),
      updatedAtTs_idx: index('orders_updatedAtTs_idx').on(table.updatedAtTs),
      updatedAtTs_idx1: index('orders_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('orders_versionNumber_idx').on(
        table.versionNumber,
      ),
      orders_generated_by_online_cart_id_idx: index(
        'orders_generated_by_online_cart_id_idx',
      ).on(table.generatedByOnlineCartId),
      orders_generated_by_customer_order_request_id_idx: index(
        'orders_generated_by_customer_order_request_id_idx',
      ).on(table.generatedByCustomerOrderRequestId),
      workflowStateId_idx: index('orders_workflowStateId_idx').on(
        table.workflowStateId,
      ),
      createdBy_idx: index('orders_createdBy_idx').on(table.createdBy),
      lastModifiedBy_idx: index('orders_lastModifiedBy_idx').on(
        table.lastModifiedBy,
      ),
      createdByRecurringServiceId_idx: index(
        'orders_createdByRecurringServiceId_idx',
      ).on(table.createdByRecurringServiceId),
      dispatchedFromRouteId_idx: index('orders_dispatchedFromRouteId_idx').on(
        table.dispatchedFromRouteId,
      ),
      deliveryAssetId_idx: index('orders_deliveryAssetId_idx').on(
        table.deliveryAssetId,
      ),
      deliveryMaterialId_idx: index('orders_deliveryMaterialId_idx').on(
        table.deliveryMaterialId,
      ),
      deliveryProductId_idx: index('orders_deliveryProductId_idx').on(
        table.deliveryProductId,
      ),
      deliverySkuId_idx: index('orders_deliverySkuId_idx').on(
        table.deliverySkuId,
      ),
      dumpTicketId_idx: index('orders_dumpTicketId_idx').on(table.dumpTicketId),
      pickupAssetId_idx: index('orders_pickupAssetId_idx').on(
        table.pickupAssetId,
      ),
      pickupMaterialId_idx: index('orders_pickupMaterialId_idx').on(
        table.pickupMaterialId,
      ),
      pickupProductId_idx: index('orders_pickupProductId_idx').on(
        table.pickupProductId,
      ),
      pickupSkuId_idx: index('orders_pickupSkuId_idx').on(table.pickupSkuId),
      /**
       * @deprecated use dumpSiteId or storedBoxOrganizationSiteId instead
       */
      postDestinationSiteId_idx: index('orders_postDestinationSiteId_idx').on(
        table.postDestinationSiteId,
      ),
      dumpSiteId_idx: index('orders_dumpSiteId_idx').on(table.dumpSiteId),
      preDestinationSiteId_idx: index('orders_preDestinationSiteId_idx').on(
        table.preDestinationSiteId,
      ),
      serviceLineId_idx: index('orders_serviceLineId_idx').on(
        table.serviceLineId,
      ),
      rootServiceLineId_idx: index('orders_rootServiceLineId_idx').on(
        table.rootServiceLineId,
      ),
      subHaulerId_idx: index('orders_subHaulerId_idx').on(table.subHaulerId),
      haulerLocationId_idx: index('orders_haulerLocationId_idx').on(
        table.haulerLocationId,
      ),
      perWorkOrderPoNumber_idx: index('orders_perWorkOrderPoNumber_idx').on(
        table.perWorkOrderPoNumber,
      ),

      adminAssignedRegularPageIdx: index(
        'orders_admin_assigned_regular_page_idx',
      )
        .on(
          table.tenantId,
          table.assignedDriverId,
          table.serviceDate,
          sql`(COALESCE(NULLIF(${table.driverRouteIndex}, 0), 1.7976931348623157e308::double precision))`,
          table.id,
        )
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.status} <> 'CANCELED'`,
        ),
      adminAssignedDumpPageIdx: index('orders_admin_assigned_dump_page_idx')
        .on(
          table.tenantId,
          table.assignedDumpDriverId,
          table.scheduledDumpDate,
          sql`(COALESCE(NULLIF(${table.driverRouteIndexForDump}, 0), 1.7976931348623157e308::double precision))`,
          table.id,
        )
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.status} <> 'CANCELED' AND ${table.separateDumpScheduling} IS TRUE`,
        ),
      adminUnassignedDumpPageIdx: index('orders_admin_unassigned_dump_page_idx')
        .on(
          table.tenantId,
          table.scheduledDumpDate,
          sql`(COALESCE(NULLIF(${table.driverRouteIndexForDump}, 0), 1.7976931348623157e308::double precision))`,
          table.id,
        )
        .where(
          sql`${table.assignedDumpDriverId} IS NULL AND ${table.separateDumpScheduling} = true AND ${table.status} <> 'CANCELED' AND ${table.archivedAtTs} IS NULL`,
        ),
      adminUnassignedPageIdx: index('orders_admin_unassigned_page_idx')
        .on(
          table.tenantId,
          table.serviceDate,
          sql`COALESCE(NULLIF(${table.driverRouteIndex}, 0), 1.7976931348623157e308::double precision)`,
          table.id,
        )
        .where(
          sql`${table.assignedDriverId} IS NULL AND ${table.status} <> 'CANCELED'`,
        ),
      assignedDumpDriverId_idx: index('orders_assignedDumpDriverId_idx').on(
        table.assignedDumpDriverId,
      ),
      consoleNavigationDumpGlobalDayIdx: index(
        'orders_console_navigation_dump_global_day_idx',
      )
        .on(
          table.serviceDate,
          table.assignedDumpDriverId,
          table.tenantId,
          table.id,
        )
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.assignedDumpDriverId} IS NOT NULL AND ${table.workflowKind} = 'SERVICE_LIQUID_WASTE' AND ${table.status} IN ('PENDING', 'IN_PROGRESS', 'SERVICED', 'INCOMPLETE', 'BILLED', 'PAUSED')`,
        ),
      consoleNavigationDumpTenantDayIdx: index(
        'orders_console_navigation_dump_tenant_day_idx',
      )
        .on(table.tenantId, table.serviceDate, table.status)
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.assignedDumpDriverId} IS NOT NULL AND ${table.workflowKind} = 'SERVICE_LIQUID_WASTE' AND ${table.status} IN ('PENDING', 'IN_PROGRESS', 'SERVICED', 'INCOMPLETE', 'BILLED', 'PAUSED')`,
        ),
      customerId_idx: index('orders_customerId_idx').on(table.customerId),
      onlineOrderId_idx: index('orders_onlineOrderId_idx').on(
        table.onlineOrderId,
      ),

      // Covering index for driver schedule query
      driver_schedule_idx: index('idx_orders_driver_schedule').on(
        table.tenantId,
        table.assignedDriverId,
        table.serviceDate,
        table.status,
        table.workflowKind,
        table.driverRouteIndex,
      ),
      tenant_serviceDate_assignedDriver_status_active_idx: index(
        'orders_tenant_service_assigned_status_active_idx',
      )
        .on(
          table.tenantId,
          table.serviceDate,
          table.assignedDriverId,
          table.status,
        )
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.assignedDriverId} IS NOT NULL`,
        ),
      gpsExpectedDumpWindowIdx: index('orders_gps_expected_dump_window_idx')
        .on(table.tenantId, table.scheduledDumpDate, table.assignedDumpDriverId)
        .where(sql`${table.archivedAtTs} IS NULL AND ${table.assignedDumpDriverId} IS NOT NULL`),
      tenant_serviceDate_assignedDriver_route_active_idx: index(
        'orders_tenant_service_assigned_route_active_idx',
      )
        .on(
          table.tenantId,
          table.serviceDate,
          table.assignedDriverId,
          table.driverRouteIndex,
        )
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.assignedDriverId} IS NOT NULL AND ${table.status} <> 'CANCELED'`,
        ),

      // Covering index for service date and status (for additional drivers query)
      service_date_status_workflow_idx: index(
        'idx_orders_service_date_status_workflow',
      ).on(table.tenantId, table.serviceDate, table.status, table.workflowKind),

      // Covering index for dump orders query
      dump_driver_schedule_idx: index('idx_orders_dump_driver_schedule').on(
        table.tenantId,
        table.separateDumpScheduling,
        table.assignedDumpDriverId,
        table.scheduledDumpDate,
        table.status,
        table.workflowKind,
        table.driverRouteIndexForDump,
      ),
      tenant_scheduled_dump_not_canceled_idx: index(
        'orders_tenant_scheduled_dump_not_canceled_idx',
      )
        .on(table.tenantId, table.scheduledDumpDate)
        .where(
          sql`${table.separateDumpScheduling} = true AND ${table.status} <> 'CANCELED' AND ${table.scheduledDumpDate} IS NOT NULL`,
        ),

      // Index for getOrder query with covering condition
      order_id_tenant_idx: index('idx_orders_id_tenant').on(
        table.id,
        table.tenantId,
      ),

      // Composite index for related queries that might join workflowState, customerSite, etc.
      order_tenant_workflow_customer_site_idx: index(
        'idx_orders_workflow_customer_join',
      ).on(table.tenantId, table.workflowStateId, table.customerSiteId),

      businessLineId_idx: index('orders_businessLineId_idx').on(
        table.businessLineId,
      ),
      serviceLineV2Id_idx: index('orders_serviceLineV2Id_idx').on(
        table.serviceLineV2Id,
      ),
      rentalChainId_idx: index('orders_rentalChainId_idx').on(
        table.rentalChainId,
      ),
      dumpVehicleId_idx: index('orders_dumpVehicleId_idx').on(
        table.dumpVehicleId,
      ),
      edgeTruckDay: index('orders_edge_truck_day')
        .on(
          table.tenantId,
          sql`COALESCE(${table.serviceVehicleId},${table.vehicleId},${table.dumpVehicleId})`,
          table.serviceDate,
          table.id,
        )
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.status} != 'CANCELED'`,
        ),
      serviceVehicleId_idx: index('orders_serviceVehicleId_idx').on(
        table.serviceVehicleId,
      ),
      orderContactId_idx: index('orders_orderContactId_idx').on(
        table.orderContactId,
      ),
      analytics_order_contact_created_idx: index(
        'orders_analytics_order_contact_created_idx',
      )
        .on(
          table.tenantId,
          table.createdAtTs.desc(),
          table.id.desc(),
          table.orderContactId,
        )
        .where(sql`${table.orderContactId} IS NOT NULL`),
      requestedByContactId_idx: index('orders_requestedByContactId_idx').on(
        table.requestedByContactId,
      ),

      //foreign keys
      fk_orders_created_by: foreignKey({
        columns: [table.createdBy],
        foreignColumns: [identity_user.id],
        name: 'fk_orders_created_by',
      }),
      fk_orders_last_modified_by: foreignKey({
        columns: [table.lastModifiedBy],
        foreignColumns: [identity_user.id],
        name: 'fk_orders_last_modified_by',
      }),
      orders_assignedDriverId_fkey: foreignKey({
        columns: [table.assignedDriverId],
        foreignColumns: [driver.id],
        name: 'orders_assignedDriverId_fkey',
      }),
      orders_assignedDumpDriverId_fkey: foreignKey({
        columns: [table.assignedDumpDriverId],
        foreignColumns: [driver.id],
        name: 'orders_assignedDumpDriverId_fkey',
      }),
      orders_createdByRecurringServiceId_fkey: foreignKey({
        columns: [table.createdByRecurringServiceId],
        foreignColumns: [recurring_service.id as AnyPgColumn],
        name: 'orders_createdByRecurringServiceId_fkey',
      }),
      orders_dispatchedFromRouteId_fkey: foreignKey({
        columns: [table.dispatchedFromRouteId],
        foreignColumns: [route.id],
        name: 'orders_dispatchedFromRouteId_fkey',
      }),
      orders_customerId_fkey: foreignKey({
        columns: [table.customerId],
        foreignColumns: [customer.id],
        name: 'orders_customerId_fkey',
      }),
      orders_customerSiteId_fkey: foreignKey({
        columns: [table.customerSiteId],
        foreignColumns: [customerSite.id],
        name: 'orders_customerSiteId_fkey',
      }),
      orders_destinationJobSiteId_fkey: foreignKey({
        columns: [table.destinationJobSiteId],
        foreignColumns: [customerSite.id],
        name: 'orders_destinationJobSiteId_fkey',
      }),
      orders_deliveryAssetId_fkey: foreignKey({
        columns: [table.deliveryAssetId],
        foreignColumns: [assets.id],
        name: 'orders_deliveryAssetId_fkey',
      }),
      orders_deliveryMaterialId_fkey: foreignKey({
        columns: [table.deliveryMaterialId],
        foreignColumns: [material.id],
        name: 'orders_deliveryMaterialId_fkey',
      }),
      orders_deliveryProductId_fkey: foreignKey({
        columns: [table.deliveryProductId],
        foreignColumns: [product.id],
        name: 'orders_deliveryProductId_fkey',
      }),
      orders_deliverySkuId_fkey: foreignKey({
        columns: [table.deliverySkuId],
        foreignColumns: [sku_v2.id as AnyPgColumn],
        name: 'orders_deliverySkuId_fkey',
      }),
      orders_dumpTicketId_fkey: foreignKey({
        columns: [table.dumpTicketId],
        foreignColumns: [dump_ticket.id],
        name: 'orders_dumpTicketId_fkey',
      }),
      orders_generatedByCustomerOrderRequestId_fkey: foreignKey({
        columns: [table.generatedByCustomerOrderRequestId],
        foreignColumns: [customer_order_request.id],
        name: 'orders_generatedByCustomerOrderRequestId_fkey',
      }),
      orders_generatedByOnlineCartId_fkey: foreignKey({
        columns: [table.generatedByOnlineCartId],
        foreignColumns: [consumer_cart.id],
        name: 'orders_generatedByOnlineCartId_fkey',
      }),
      orders_haulerLocationId_fkey: foreignKey({
        columns: [table.haulerLocationId],
        foreignColumns: [hauler_location.id],
        name: 'orders_haulerLocationId_fkey',
      }),
      orders_pickupAssetId_fkey: foreignKey({
        columns: [table.pickupAssetId],
        foreignColumns: [assets.id],
        name: 'orders_pickupAssetId_fkey',
      }),
      orders_pickupMaterialId_fkey: foreignKey({
        columns: [table.pickupMaterialId],
        foreignColumns: [material.id],
        name: 'orders_pickupMaterialId_fkey',
      }),
      orders_pickupProductId_fkey: foreignKey({
        columns: [table.pickupProductId],
        foreignColumns: [product.id],
        name: 'orders_pickupProductId_fkey',
      }),
      orders_pickupSkuId_fkey: foreignKey({
        columns: [table.pickupSkuId],
        foreignColumns: [sku_v2.id as AnyPgColumn],
        name: 'orders_pickupSkuId_fkey',
      }),

      /**
       * @deprecated use dumpSiteId or storedBoxOrganizationSiteId instead
       */
      orders_postDestinationSiteId_fkey: foreignKey({
        columns: [table.postDestinationSiteId],
        foreignColumns: [organization_site.id],
        name: 'orders_postDestinationSiteId_fkey',
      }),
      orders_dumpSiteId_fkey: foreignKey({
        columns: [table.dumpSiteId],
        foreignColumns: [organization_site.id],
        name: 'orders_dumpSiteId_fkey',
      }),
      orders_preDestinationSiteId_fkey: foreignKey({
        columns: [table.preDestinationSiteId],
        foreignColumns: [organization_site.id],
        name: 'orders_preDestinationSiteId_fkey',
      }),
      //orders_serviceLineId_service_line_id_fk
      // orders_serviceLineId_fkey: foreignKey({
      //   columns: [table.serviceLineId],
      //   foreignColumns: [service_line.id as AnyPgColumn],
      //   name: 'orders_serviceLineId_fkey',
      // }),
      orders_rootServiceLineId_fkey: foreignKey({
        columns: [table.rootServiceLineId],
        foreignColumns: [service_line.id as AnyPgColumn],
        name: 'orders_rootServiceLineId_fkey',
      }),
      orders_subHaulerId_fkey: foreignKey({
        columns: [table.subHaulerId],
        foreignColumns: [sub_hauler.id],
        name: 'orders_subHaulerId_fkey',
      }),
      orders_workflowStateId_fkey: foreignKey({
        columns: [table.workflowStateId],
        foreignColumns: [workflow_state.id],
        name: 'orders_workflowStateId_fkey',
      }),
      urgentNoteScheduleIdx: index('orders_urgent_note_schedule_idx').on(table.tenantId, table.assignedDriverId, table.serviceDate).where(sql`${table.isDriverNoteUrgent} = true AND ${table.archivedAtTs} IS NULL`),
      recurringWorkOrderId_idx: index('orders_recurringWorkOrderId_idx').on(
        table.recurringWorkOrderId,
      ),
      fk_orders_recurring_work_order: foreignKey({
        columns: [table.recurringWorkOrderId],
        foreignColumns: [recurringWorkOrder.id],
        name: 'fk_orders_recurring_work_order',
      }),

      // Check constraint to enforce business rule for customerSiteId
      orders_customer_site_id_workflow_kind_check: check(
        'orders_customer_site_id_workflow_kind_check',
        sql`(${table.workflowKind} = 'DUMP' AND ${table.customerSiteId} IS NULL) OR (${table.workflowKind} != 'DUMP' AND ${table.customerSiteId} IS NOT NULL)`,
      ),

      // Order type config
      orderTypeConfigId_idx: index('orders_orderTypeConfigId_idx').on(
        table.orderTypeConfigId,
      ),
      orders_orderTypeConfigId_fkey: foreignKey({
        columns: [table.orderTypeConfigId],
        foreignColumns: [order_type_config.id],
        name: 'orders_orderTypeConfigId_fkey',
      }),
    };
  },
);

export const orders_history = pgTable(
  'orders_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    vehicleId: uuid('vehicleId'),
    requestedByContactId: uuid('requestedByContactId'),
    recurringWorkOrderId: uuid('recurringWorkOrderId'),
    number: integer('number'),
    assignedDriverId: uuid('assignedDriverId'),
    dispatchNotes: text('dispatchNotes'),
    isDriverNoteUrgent: boolean('isDriverNoteUrgent'),
    driverNotes: text('driverNotes'),
    driverNotesRich: jsonb('driverNotesRich'),
    serviceDate: date('serviceDate'),
    initiallyRequestedDate: date('initiallyRequestedDate'),
    status: order_status('status'),
    totalDays: integer('totalDays'),
    driverRouteIndex: doublePrecision('driverRouteIndex'),
    preDestinationSiteId: uuid('preDestinationSiteId'),
    workflowStateId: uuid('workflowStateId'),
    workflowKind: workflow_kind('workflowKind'),
    pricingModel: order_pricing_model('pricingModel'),
    incompleteReason: text('incompleteReason'),
    shouldPreDestinationUseLastJob: boolean('shouldPreDestinationUseLastJob'),
    /**
     * @deprecated use dumpSiteId or storedBoxOrganizationSiteId instead
     */
    postDestinationSiteId: uuid('postDestinationSiteId'),
    dumpSiteId: uuid('dumpSiteId'),
    skipDumpStep: boolean('skipDumpStep'),
    customerSiteId: uuid('customerSiteId'),
    destinationJobSiteId: uuid('destinationJobSiteId'),
    lastModifiedBy: uuid('lastModifiedBy'),
    preferredTimeEnd: time('preferredTimeEnd'),
    preferredTimeStart: time('preferredTimeStart'),
    createdBy: uuid('createdBy'),
    onlineOrderId: uuid('onlineOrderId'),
    customReferenceId: varchar('customReferenceId', { length: 255 }),
    scheduledDumpDate: date('scheduledDumpDate'),
    assignedDumpDriverId: uuid('assignedDumpDriverId'),
    driverRouteIndexForDump: doublePrecision('driverRouteIndexForDump'),
    separateDumpScheduling: boolean('separateDumpScheduling'),
    searchables2: text('searchables2'),
    serviceLineId: uuid('serviceLineId'),
    rootServiceLineId: uuid('rootServiceLineId'),
    generatedByRoute: boolean('generatedByRoute'),
    createdByRecurringServiceId: uuid('createdByRecurringServiceId'),
    dispatchedFromRouteId: uuid('dispatchedFromRouteId'),
    isCompleted: boolean('isCompleted'),
    orderNumber: varchar('orderNumber', { length: 255 }),
    customOrderDescription: text('customOrderDescription'),
    flagDispatchNotes: boolean('flagDispatchNotes'),
    workflowEndsService: boolean('workflowEndsService'),
    workflowStartsService: boolean('workflowStartsService'),
    customerId: uuid('customerId'),
    driverAddedNotes: text('driverAddedNotes'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    generatedByOnlineCartId: uuid('generatedByOnlineCartId'),
    freezeCharges: boolean('freezeCharges'),
    generatedByCustomerOrderRequestId: uuid(
      'generatedByCustomerOrderRequestId',
    ),
    dumpTicketId: uuid('dumpTicketId'),
    fakeColumn: boolean('fakeColumn'),
    completedAt: timestamp('completedAt', {
      withTimezone: true,
      mode: 'string',
    }),
    pickupDateBillingOverride: date('pickupDateBillingOverride'),
    subHaulerId: uuid('subHaulerId'),
    deliveryProductId: uuid('deliveryProductId'),
    deliveryAssetId: uuid('deliveryAssetId'),
    deliverySkuId: uuid('deliverySkuId'),
    deliveryMaterialId: uuid('deliveryMaterialId'),
    pickupProductId: uuid('pickupProductId'),
    pickupAssetId: uuid('pickupAssetId'),
    pickupSkuId: uuid('pickupSkuId'),
    pickupMaterialId: uuid('pickupMaterialId'),
    scheduledPickupDate: date('scheduledPickupDate'),
    haulerLocationId: uuid('haulerLocationId'),
    perWorkOrderPoNumber: varchar('perWorkOrderPoNumber', { length: 255 }),
    quantity: integer('quantity').default(0).notNull(),
    unpaidBalanceCents: bigint('unpaidBalanceCents', { mode: 'number' }),
    storedBoxOrganizationSiteId: uuid('storedBoxOrganizationSiteId'),
    dumpVehicleId: uuid('dumpVehicleId'),
    serviceVehicleId: uuid('serviceVehicleId'),
    orderContactId: uuid('orderContactId'),

    // Cost tracking fields
    estimatedDurationMinutes: integer('estimatedDurationMinutes'),
    actualDurationMinutes: integer('actualDurationMinutes'),
    actualStartTime: timestamp('actualStartTime', { withTimezone: true }),
    actualEndTime: timestamp('actualEndTime', { withTimezone: true }),
    startMileage: integer('startMileage'),
    endMileage: integer('endMileage'),
    totalMiles: doublePrecision('totalMiles'),
    rentalChainId: uuid('rentalChainId'),
    serviceLineV2Id: uuid('serviceLineV2Id'),
    businessLineId: uuid('businessLineId'),
    orderTypeConfigId: uuid('orderTypeConfigId'),
    plannedStartTime: time('plannedStartTime'),
  },
  (table) => {
    return {
      id_version_number_idx: index('orders_history_id_version_number_idx').on(
        table.id,
        table.versionNumber,
      ),
      createdByRecurringServiceId_idx: index(
        'orders_history_createdByRecurringServiceId_idx',
      ).on(table.createdByRecurringServiceId),
      dispatchedFromRouteId_idx: index(
        'orders_history_dispatchedFromRouteId_idx',
      ).on(table.dispatchedFromRouteId),
      dumpTicketId_idx: index('orders_history_dumpTicketId_idx').on(
        table.dumpTicketId,
      ),
      onlineOrderId_idx: index('orders_history_onlineOrderId_idx').on(
        table.onlineOrderId,
      ),
      serviceLineId_idx: index('orders_history_serviceLineId_idx').on(
        table.serviceLineId,
      ),
      rootServiceLineId_idx: index('orders_history_rootServiceLineId_idx').on(
        table.rootServiceLineId,
      ),
      haulerLocationId_idx: index('orders_history_haulerLocationId_idx').on(
        table.haulerLocationId,
      ),
      perWorkOrderPoNumber_idx: index(
        'orders_history_perWorkOrderPoNumber_idx',
      ).on(table.perWorkOrderPoNumber),
    };
  },
);

export type OrderSchema = typeof orders.$inferSelect;

export const order_driver_note_acknowledgement = pgTable('order_driver_note_acknowledgement', {
 id: uuid('id').defaultRandom().primaryKey(),
 tenantId: uuid('tenantId').notNull(),
 orderId: uuid('orderId').notNull().references(() => orders.id),
 identityUserId: uuid('identityUserId').notNull().references(() => identity_user.id),
 noteText: text('noteText').notNull(),
 acknowledgedAt: timestamp('acknowledgedAt', { withTimezone: true }).notNull(),
 receivedAt: timestamp('receivedAt', { withTimezone: true }).notNull().defaultNow(),
 requestId: uuid('requestId').notNull(),
}, (table) => ({
 requestUnique: unique('urgent_driver_note_ack_request_unique').on(table.tenantId, table.identityUserId, table.requestId),
 orderIdx: index('order_driver_note_ack_order_idx').on(table.tenantId, table.orderId, table.identityUserId, table.receivedAt.desc()),
}));
